import { useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import type { OidcScope } from '@shared/oidc';
import { api, apiUrl } from '../lib/api';
import { useAsync } from '../lib/useAsync';
import { Button, Card, CardHeader, Skeleton, StatBlock } from '../components/ui';
import { Notice } from '../components/form';

const SCOPE_LABELS: Record<OidcScope, string> = {
  openid: '确认你的身份（必需）',
  profile: '读取你的姓名',
  email: '读取你的邮箱',
  org: '读取你所属的组织与组织内角色',
};

interface ConsentInfo {
  client: { clientId: string; name: string };
  scopes: OidcScope[];
  tenantName: string;
  userEmail: string;
}

/**
 * 授权确认。
 *
 * 点「授权」实际上是带着全部原始参数（外加 `consent=granted`）重新访问 /authorize，
 * 服务端随即发码并回跳。这样做整条链路无状态、不需要 CSRF 令牌。
 */
export function ConsentPage() {
  const [params] = useSearchParams();
  const [busy, setBusy] = useState(false);

  const query = params.toString();
  const info = useAsync<ConsentInfo>(() => api.get(`/api/oidc/authorize-info?${query}`), [query]);

  const authorizeUrl = apiUrl(
    `/authorize?${new URLSearchParams({ ...Object.fromEntries(params), consent: 'granted' }).toString()}`,
  );
  const denyUrl = (() => {
    const redirectUri = params.get('redirect_uri');
    if (!redirectUri) return '/';
    const target = new URL(redirectUri);
    target.searchParams.set('error', 'access_denied');
    const state = params.get('state');
    if (state) target.searchParams.set('state', state);
    return target.toString();
  })();

  return (
    <div className="flex min-h-full items-center justify-center bg-canvas p-4">
      <div className="w-full max-w-lg">
        <Card>
          <CardHeader title="授权请求" hint="确认后，该应用就能用你的身份登录" />

          {info.loading ? (
            <Skeleton className="h-40 w-full" />
          ) : info.error ? (
            <Notice tone="danger">{info.error.message}</Notice>
          ) : (
            <>
              <p className="text-[15px] text-fg">
                <span className="font-semibold">{info.data?.client.name}</span>
                <span className="text-fg-muted"> 请求以你的身份登录</span>
              </p>

              <div className="mt-4 grid gap-3 sm:grid-cols-2">
                <StatBlock label="你的账号" value={<span className="text-[13px]">{info.data?.userEmail}</span>} />
                <StatBlock label="所属组织" value={<span className="text-[13px]">{info.data?.tenantName}</span>} />
              </div>

              <div className="mt-5">
                <p className="mb-2 text-[13px] font-medium text-fg">它将获得的信息</p>
                <ul className="space-y-1.5">
                  {info.data?.scopes.map((scope) => (
                    <li key={scope} className="flex items-center gap-2 rounded-control bg-nest-2 px-3 py-2 text-[13px]">
                      <span className="font-mono text-xs text-fg-muted">{scope}</span>
                      <span className="text-fg">{SCOPE_LABELS[scope]}</span>
                    </li>
                  ))}
                </ul>
              </div>

              <div className="mt-5 flex flex-col gap-2 sm:flex-row">
                <a
                  href={authorizeUrl}
                  className="flex-1"
                  onClick={() => setBusy(true)}
                  aria-busy={busy}
                >
                  <Button variant="primary" block>
                    授权
                  </Button>
                </a>
                <a href={denyUrl} className="flex-1">
                  <Button block>拒绝</Button>
                </a>
              </div>

              <p className="mt-4 text-xs text-fg-muted">
                授权结果会记录在操作记录里，可以随时在「应用与接入」里停用该应用。
              </p>
            </>
          )}
        </Card>
      </div>
    </div>
  );
}
